<script setup lang="ts">
// Login / register / reset-password dialog. Markup, class names and copy follow production
// (assets/js/auth.js showLogin + assets/desktop-ui/auth.js); the generated legacy CSS styles it.
// The dialog is shared by every entry point (account pill, gated actions, /login /register /reset-password).
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { api, ApiError } from '../api'
import { loadSession } from '../stores/session'
import { closeAuth, type AuthMode } from '../stores/overlays'
import { isDesktop } from '../shell/useShell'

const props = defineProps<{ mode: AuthMode; method: 'code' | 'password'; message: string }>()

const view = ref<AuthMode>(props.mode)
const method = ref<'code' | 'password'>(props.method)
const submitting = ref(false)
const intro = ref('')
const wechatAvailable = ref(false)
const wechatOpen = ref(false)
const root = ref<HTMLElement | null>(null)

const form = reactive({
  loginEmail: '', loginCode: '', loginPassword: '',
  registerName: '', registerEmail: '', registerCode: '', registerPassword: '', registerConfirm: '', legal: false,
  resetEmail: '', resetCode: '', resetPassword: '', resetConfirm: '',
  wechatLegal: false,
})
const errors = reactive({ login: props.message, register: '', reset: '', wechat: '' })
const feedback = reactive({ login: '', register: '', reset: '' })
const shown = reactive({ login: false, register: false, reset: false, wechat: false })
const reveal = reactive({ login: false, register: false, registerConfirm: false, reset: false, resetConfirm: false })

const title = computed(() => wechatOpen.value ? '微信快捷登录' : view.value === 'register' ? '创建账号' : view.value === 'reset' ? '重置密码' : '欢迎回来')
const introText = computed(() => intro.value || (wechatOpen.value ? '使用微信快捷登录并继续保存学习进度。'
  : view.value === 'register' ? '验证邮箱并设置密码，保存你的学习进度。'
  : view.value === 'reset' ? '验证账号邮箱，设置新的登录密码。' : '登录账号，继续你的学习进度。'))

// Code cooldowns are per purpose and email, as in production (60 s after a request).
const cooldowns = reactive<Record<string, number>>({})
const busyKey = ref('')
const now = ref(Date.now())
let ticker = 0
const key = (purpose: string, email: string) => `${purpose}:${email.trim().toLowerCase()}`
const remaining = (purpose: string, email: string) => Math.max(0, Math.ceil(((cooldowns[key(purpose, email)] || 0) - now.value) / 1000))
function sendLabel(purpose: string, email: string) {
  if (busyKey.value === purpose) return '发送中…'
  const left = remaining(purpose, email)
  return left > 0 ? `重新发送（${left}s）` : '获取验证码'
}
const sendDisabled = (purpose: string, email: string) => busyKey.value === purpose || remaining(purpose, email) > 0

function failure(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return `尝试过于频繁，请在 ${(error.details as { retryAfter?: number } | undefined)?.retryAfter || '稍后'} 秒后再试。`
    if (error.status >= 500) return '连接失败或服务暂不可用，请稍后重试。'
    return fallback
  }
  return '连接失败或服务暂不可用，请稍后重试。'
}

async function sendCode(purpose: 'login' | 'register' | 'reset') {
  const email = purpose === 'login' ? form.loginEmail : purpose === 'register' ? form.registerEmail : form.resetEmail
  if (sendDisabled(purpose, email)) return
  errors[purpose] = ''
  feedback[purpose] = ''
  shown[purpose] = false
  busyKey.value = purpose
  const submitted = email
  try {
    if (purpose === 'login') await api.account.requestLoginCode(email.trim())
    else if (purpose === 'register') await api.accountCenter.requestSignupCode(email.trim())
    else await api.accountCenter.requestPasswordReset(email.trim())
    cooldowns[key(purpose, submitted)] = Date.now() + 60000
    feedback[purpose] = purpose === 'register' ? '请求已受理，请查看邮箱验证码。' : '请求已受理；若邮箱已注册且邮件可投递，将收到验证码。未收到请稍后重试。'
    shown[purpose] = true
  } catch (error) {
    errors[purpose] = error instanceof ApiError && error.code === 'INVALID_EMAIL' ? '请输入有效邮箱'
      : error instanceof ApiError && error.code === 'EMAIL_PROVIDER_NOT_CONFIGURED' ? '邮件服务尚未配置，请联系管理员。'
      : error instanceof ApiError && error.status === 429 ? '尝试过于频繁，请稍后再试。'
      : !(error instanceof ApiError) || error.status >= 500 ? '连接失败或服务暂不可用，请稍后重试。' : '发送失败，请稍后重试。'
  } finally {
    busyKey.value = ''
  }
}

function setTopMode(next: 'login' | 'register') {
  if (submitting.value) return
  view.value = next
  wechatOpen.value = false
  intro.value = ''
  errors.login = errors.register = ''
  Object.assign(form, { loginCode: '', loginPassword: '', registerCode: '', registerPassword: '', registerConfirm: '' })
  void nextTick(() => root.value?.querySelector<HTMLInputElement>(next === 'register' ? '#registerName' : '#loginEmail')?.focus())
}
function switchMethod(next: 'code' | 'password') {
  if (submitting.value) return
  method.value = next
  errors.login = ''
  shown.login = false
  feedback.login = ''
  if (next === 'password') form.loginCode = ''
  else form.loginPassword = ''
  void nextTick(() => root.value?.querySelector<HTMLInputElement>(next === 'password' ? '#loginPassword' : '#loginCode')?.focus())
}
function showReset() {
  if (submitting.value) return
  form.resetEmail = view.value === 'register' ? form.registerEmail : form.loginEmail
  view.value = 'reset'
  intro.value = ''
  errors.reset = ''
  shown.reset = false
  void nextTick(() => root.value?.querySelector<HTMLInputElement>('#resetEmail')?.focus())
}
function backToPasswordLogin() {
  if (submitting.value) return
  const email = form.resetEmail
  setTopMode('login')
  switchMethod('password')
  Object.assign(form, { loginEmail: email, resetCode: '', resetPassword: '', resetConfirm: '', loginPassword: '' })
  void nextTick(() => root.value?.querySelector<HTMLInputElement>('#loginPassword')?.focus())
}

async function finish() {
  await loadSession()
  closeAuth('success')
}

async function submitLogin() {
  if (submitting.value) return
  const asMethod = method.value
  errors.login = ''
  shown.login = false
  submitting.value = true
  try {
    if (asMethod === 'password') await api.account.passwordLogin(form.loginEmail.trim(), form.loginPassword)
    else await api.account.codeLogin(form.loginEmail.trim(), form.loginCode.trim())
    await finish()
  } catch (error) {
    errors.login = failure(error, asMethod === 'password' ? '邮箱或密码不正确。' : '验证码无效或已过期。')
    void nextTick(() => root.value?.querySelector<HTMLInputElement>(asMethod === 'password' ? '#loginPassword' : '#loginCode')?.select())
  } finally {
    submitting.value = false
  }
}

async function submitRegister() {
  if (submitting.value) return
  errors.register = ''
  if (form.registerPassword !== form.registerConfirm) { errors.register = '两次输入的密码不一致。'; return }
  submitting.value = true
  try {
    await api.accountCenter.signup({
      email: form.registerEmail.trim(), code: form.registerCode.trim(), password: form.registerPassword,
      displayName: form.registerName.trim() || undefined, avatar: 'default',
    })
    await finish()
  } catch (error) {
    if (error instanceof ApiError && error.code === 'EMAIL_ALREADY_REGISTERED') {
      submitting.value = false
      setTopMode('login')
      form.loginEmail = form.registerEmail
      switchMethod('password')
      intro.value = '该邮箱已注册，请直接登录；忘记密码可重置。'
      return
    }
    const code = error instanceof ApiError ? error.code : ''
    errors.register = code === 'WEAK_PASSWORD' ? '密码至少 8 位，并同时包含字母和数字。'
      : code === 'INVALID_CREDENTIALS' ? '该邮箱已有未完成验证的账号，请使用原密码或重置密码。'
      : failure(error, '验证码无效或已过期，请重新获取。')
  } finally {
    submitting.value = false
  }
}

async function submitReset() {
  if (submitting.value) return
  errors.reset = ''
  if (form.resetPassword !== form.resetConfirm) { errors.reset = '两次输入的密码不一致。'; return }
  submitting.value = true
  try {
    await api.accountCenter.resetPassword(form.resetEmail.trim(), form.resetCode.trim(), form.resetPassword, form.resetConfirm)
    const email = form.resetEmail
    submitting.value = false
    setTopMode('login')
    form.loginEmail = email
    switchMethod('password')
    intro.value = '密码已重置，请使用新密码登录。'
  } catch (error) {
    const code = error instanceof ApiError ? error.code : ''
    errors.reset = code === 'WEAK_PASSWORD' ? '密码至少 8 位，并同时包含字母和数字。'
      : error instanceof ApiError && error.status === 429 ? '尝试次数过多，请稍后再试。'
      : failure(error, '验证码无效或已过期。')
  } finally {
    submitting.value = false
  }
}

async function openWechat() {
  if (submitting.value) return
  wechatOpen.value = true
  errors.wechat = ''
  intro.value = ''
}
function closeWechat() {
  wechatOpen.value = false
  intro.value = ''
}
async function startWechat() {
  errors.wechat = ''
  if (!form.wechatLegal) { errors.wechat = '请先阅读并同意用户协议和隐私政策。'; return }
  try {
    const result = await api.accountCenter.wechatWebStart()
    window.location.href = result.authorizeUrl
  } catch {
    errors.wechat = '微信登录暂不可用，请稍后重试或改用邮箱登录。'
  }
}

function cancel() { closeAuth('cancel') }

// Escape closes, Tab stays inside the dialog (production installModal).
function focusable(): HTMLElement[] {
  const all = root.value?.querySelectorAll<HTMLElement>('button,input,select,textarea,a[href],[tabindex]:not([tabindex="-1"])') ?? []
  return [...all].filter(el => !(el as HTMLInputElement).disabled && !el.closest('[hidden]') && el.getClientRects().length)
}
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') { event.preventDefault(); cancel(); return }
  if (event.key !== 'Tab') return
  const items = focusable()
  if (!items.length) { event.preventDefault(); return }
  const first = items[0], last = items[items.length - 1]
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}
function onFocusIn(event: FocusEvent) {
  if (root.value && !root.value.contains(event.target as Node)) focusable()[0]?.focus()
}

let previous: Element | null = null
onMounted(() => {
  previous = document.activeElement
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('focusin', onFocusIn)
  ticker = window.setInterval(() => { now.value = Date.now() }, 1000)
  document.body.classList.add('modal-open')
  void nextTick(() => root.value?.querySelector<HTMLInputElement>(view.value === 'register' ? '#registerName' : view.value === 'reset' ? '#resetEmail' : method.value === 'password' ? '#loginEmail' : '#loginEmail')?.focus())
  api.accountCenter.wechatWebStart().then(() => { wechatAvailable.value = true }).catch(() => { wechatAvailable.value = false })
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('focusin', onFocusIn)
  window.clearInterval(ticker)
  document.body.classList.remove('modal-open')
  if (previous instanceof HTMLElement && previous.isConnected) previous.focus()
})
</script>

<template>
  <div id="authOverlay" ref="root" class="auth-overlay" :data-desktop-auth-enhanced="isDesktop ? '1' : undefined" :data-desktop-auth-handlers="isDesktop ? '1' : undefined" @click.self="cancel">
    <section class="auth-card" role="dialog" aria-modal="true" aria-labelledby="authTitle" :aria-describedby="isDesktop ? 'authIntro' : undefined">
      <aside v-if="isDesktop" class="desktop-auth-brand" aria-label="导游学霸账号">
        <img src="/assets/desktop-ui/logo.png" alt="" loading="lazy" decoding="async">
        <b>导游学霸</b>
        <span class="desktop-auth-tagline">保存每一次练习，<br>按自己的节奏备考</span>
        <div class="desktop-auth-benefits">
          <div class="desktop-auth-benefit">
            <span class="desktop-auth-benefit-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 19V12M10 19V7M15 19V4M20 19V10" /></svg></span>
            <span class="desktop-auth-benefit-copy"><b>学习进度保存</b><small>随时记录，持续进步</small></span>
          </div>
          <div class="desktop-auth-benefit">
            <span class="desktop-auth-benefit-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="2.5" y="4" width="14" height="15" rx="2" /><path d="M6 16h7M17 8h4.5v12H14v-1" /></svg></span>
            <span class="desktop-auth-benefit-copy"><b>跨设备同步</b><small>手机电脑，随时学习</small></span>
          </div>
        </div>
        <div class="desktop-auth-landscape" aria-hidden="true"></div>
        <small>GUIDE · LEARN · PASS</small>
      </aside>
      <div class="auth-mobile-brand" aria-label="导游学霸账号">
        <span class="auth-brand-mark" aria-hidden="true">学</span>
        <div><b>导游学霸</b><small>保存每一次练习，继续备考</small></div>
      </div>
      <span class="auth-kicker">{{ isDesktop ? '导游学霸账号' : 'PRIVATE BETA' }}</span>
      <h2 id="authTitle">{{ title }}</h2>
      <p id="authIntro">{{ introText }}</p>
      <div class="auth-provider-note auth-notice" role="note">
        <span class="auth-provider-dot" aria-hidden="true"></span>
        <span>登录后学习进度会跨设备同步。</span>
      </div>
      <div v-show="view !== 'reset' && !wechatOpen" class="auth-tabs" role="tablist" aria-label="账号操作">
        <button type="button" :class="{ active: view === 'login' }" role="tab" :aria-selected="view === 'login'" aria-controls="authLoginForm" :disabled="submitting" @click="setTopMode('login')">登录</button>
        <button type="button" :class="{ active: view === 'register' }" role="tab" :aria-selected="view === 'register'" aria-controls="authRegisterForm" :disabled="submitting" @click="setTopMode('register')">注册</button>
      </div>

      <form v-show="view === 'login' && !wechatOpen" id="authLoginForm" role="tabpanel" @submit.prevent="submitLogin">
        <div class="auth-field">
          <label for="loginEmail">邮箱</label>
          <input id="loginEmail" v-model="form.loginEmail" name="email" type="email" autocomplete="email" placeholder="请输入邮箱地址" required>
        </div>
        <div v-show="method === 'code'" class="auth-field auth-login-code-field">
          <div class="auth-label-row">
            <label for="loginCode">邮箱验证码</label>
            <button type="button" class="auth-method-switch" :disabled="submitting" @click="switchMethod('password')">用密码登录</button>
          </div>
          <div class="auth-code-row">
            <input id="loginCode" v-model="form.loginCode" name="code" inputmode="numeric" pattern="[0-9]{6}" minlength="6" maxlength="6" autocomplete="one-time-code" placeholder="6位验证码" :disabled="method !== 'code'" :required="method === 'code'">
            <button id="authLoginSend" type="button" :disabled="sendDisabled('login', form.loginEmail)" @click="sendCode('login')">{{ sendLabel('login', form.loginEmail) }}</button>
          </div>
          <small class="auth-field-hint">验证码10分钟内有效</small>
        </div>
        <div v-show="method === 'password'" class="auth-field auth-login-password-field">
          <div class="auth-label-row">
            <label for="loginPassword">密码</label>
            <button type="button" class="auth-method-switch" :disabled="submitting" @click="switchMethod('code')">用验证码登录</button>
          </div>
          <span v-if="isDesktop" class="desktop-password-field">
            <input id="loginPassword" v-model="form.loginPassword" name="password" :type="reveal.login ? 'text' : 'password'" autocomplete="current-password" placeholder="请输入密码" :disabled="method !== 'password'" :required="method === 'password'" data-desktop-password-enhanced="1">
            <button type="button" class="desktop-password-toggle" :aria-label="reveal.login ? '隐藏密码' : '显示密码'" :aria-pressed="reveal.login" @click="reveal.login = !reveal.login">
              <svg v-if="!reveal.login" viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.3-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.3 6.5-9.5 6.5S2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.7" /></svg>
              <svg v-else viewBox="0 0 24 24" aria-hidden="true"><path d="m3 3 18 18M10.6 5.7c.5-.1.9-.2 1.4-.2 6.2 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.1 3.8M6.2 6.2C3.8 7.7 2.5 12 2.5 12s3.3 6.5 9.5 6.5c1.1 0 2.1-.2 3-.6" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /></svg>
            </button>
          </span>
          <input v-else id="loginPassword" v-model="form.loginPassword" name="password" type="password" autocomplete="current-password" placeholder="请输入密码" :disabled="method !== 'password'" :required="method === 'password'">
          <button type="button" class="auth-forgot" :disabled="submitting" @click="showReset">忘记密码？</button>
        </div>
        <div class="auth-error" :class="{ 'desktop-auth-error': isDesktop }" role="alert" aria-live="assertive">{{ errors.login }}</div>
        <div class="auth-notice auth-feedback" role="status" aria-live="polite" :hidden="!shown.login">{{ feedback.login }}</div>
        <button type="submit" class="auth-primary" :disabled="submitting">{{ submitting ? '登录中…' : '登录' }}</button>
        <button v-if="isDesktop" type="button" class="auth-wechat-link" :hidden="!wechatAvailable" :disabled="submitting" @click="openWechat">微信快捷登录</button>
      </form>

      <form v-show="view === 'register' && !wechatOpen" id="authRegisterForm" role="tabpanel" @submit.prevent="submitRegister">
        <div class="auth-field-pair">
          <div class="auth-field">
            <label for="registerName">昵称</label>
            <input id="registerName" v-model="form.registerName" name="displayName" type="text" autocomplete="nickname" maxlength="24" placeholder="例如：小赣导游" required>
          </div>
          <div class="auth-field">
            <label for="registerEmail">邮箱</label>
            <input id="registerEmail" v-model="form.registerEmail" name="email" type="email" autocomplete="email" placeholder="请输入邮箱地址" required>
          </div>
        </div>
        <div class="auth-field">
          <label for="authCode">邮箱验证码</label>
          <div class="auth-code-row">
            <input id="authCode" v-model="form.registerCode" name="code" inputmode="numeric" pattern="[0-9]{6}" minlength="6" maxlength="6" autocomplete="one-time-code" placeholder="6位验证码" required>
            <button id="authSend" type="button" :disabled="sendDisabled('register', form.registerEmail)" @click="sendCode('register')">{{ sendLabel('register', form.registerEmail) }}</button>
          </div>
          <small class="auth-field-hint">验证码10分钟内有效</small>
        </div>
        <div class="auth-field-pair">
          <div class="auth-field">
            <label for="registerPassword">设置密码</label>
            <span v-if="isDesktop" class="desktop-password-field">
              <input id="registerPassword" v-model="form.registerPassword" name="password" :type="reveal.register ? 'text' : 'password'" minlength="8" autocomplete="new-password" placeholder="至少8位，含字母和数字" required data-desktop-password-enhanced="1">
              <button type="button" class="desktop-password-toggle" :aria-label="reveal.register ? '隐藏密码' : '显示密码'" :aria-pressed="reveal.register" @click="reveal.register = !reveal.register">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.3-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.3 6.5-9.5 6.5S2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.7" /></svg>
              </button>
            </span>
            <input v-else id="registerPassword" v-model="form.registerPassword" name="password" type="password" minlength="8" autocomplete="new-password" placeholder="至少8位，含字母和数字" required>
          </div>
          <div class="auth-field">
            <label for="registerPasswordConfirm">确认密码</label>
            <span v-if="isDesktop" class="desktop-password-field">
              <input id="registerPasswordConfirm" v-model="form.registerConfirm" name="passwordConfirm" :type="reveal.registerConfirm ? 'text' : 'password'" minlength="8" autocomplete="new-password" placeholder="再次输入密码" required data-desktop-password-enhanced="1">
              <button type="button" class="desktop-password-toggle" :aria-label="reveal.registerConfirm ? '隐藏密码' : '显示密码'" :aria-pressed="reveal.registerConfirm" @click="reveal.registerConfirm = !reveal.registerConfirm">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.3-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.3 6.5-9.5 6.5S2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.7" /></svg>
              </button>
            </span>
            <input v-else id="registerPasswordConfirm" v-model="form.registerConfirm" name="passwordConfirm" type="password" minlength="8" autocomplete="new-password" placeholder="再次输入密码" required>
          </div>
        </div>
        <label class="auth-legal-consent">
          <input v-model="form.legal" name="acceptLegal" type="checkbox" required>
          <span>我已阅读并同意 <a href="/user-agreement.html" target="_blank" rel="noopener">《用户协议》</a>和<a href="/privacy.html" target="_blank" rel="noopener">《隐私政策》</a></span>
        </label>
        <div class="auth-error" :class="{ 'desktop-auth-error': isDesktop }" role="alert" aria-live="assertive">{{ errors.register }}</div>
        <div class="auth-notice auth-register-feedback" role="status" aria-live="polite" :hidden="!shown.register">{{ feedback.register }}</div>
        <button type="submit" class="auth-primary" :disabled="submitting">{{ submitting ? '注册中…' : '注册并登录' }}</button>
      </form>

      <form v-show="view === 'reset' && !wechatOpen" id="authResetForm" @submit.prevent="submitReset">
        <div class="auth-reset-heading-back"><button type="button" class="auth-back-login" :disabled="submitting" @click="backToPasswordLogin">‹ 返回密码登录</button></div>
        <div class="auth-field">
          <label for="resetEmail">邮箱</label>
          <input id="resetEmail" v-model="form.resetEmail" name="email" type="email" autocomplete="email" placeholder="请输入邮箱地址" required>
        </div>
        <div class="auth-field">
          <label for="resetCode">邮箱验证码</label>
          <div class="auth-code-row">
            <input id="resetCode" v-model="form.resetCode" name="code" inputmode="numeric" pattern="[0-9]{6}" minlength="6" maxlength="6" autocomplete="one-time-code" placeholder="6位验证码" required>
            <button id="authResetSend" type="button" :disabled="sendDisabled('reset', form.resetEmail)" @click="sendCode('reset')">{{ sendLabel('reset', form.resetEmail) }}</button>
          </div>
          <small class="auth-field-hint">验证码10分钟内有效</small>
        </div>
        <div class="auth-field-pair">
          <div class="auth-field">
            <label for="resetPassword">新密码</label>
            <span v-if="isDesktop" class="desktop-password-field">
              <input id="resetPassword" v-model="form.resetPassword" name="password" :type="reveal.reset ? 'text' : 'password'" minlength="8" autocomplete="new-password" placeholder="至少8位，含字母和数字" required data-desktop-password-enhanced="1">
              <button type="button" class="desktop-password-toggle" :aria-label="reveal.reset ? '隐藏密码' : '显示密码'" :aria-pressed="reveal.reset" @click="reveal.reset = !reveal.reset">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.3-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.3 6.5-9.5 6.5S2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.7" /></svg>
              </button>
            </span>
            <input v-else id="resetPassword" v-model="form.resetPassword" name="password" type="password" minlength="8" autocomplete="new-password" placeholder="至少8位，含字母和数字" required>
          </div>
          <div class="auth-field">
            <label for="resetPasswordConfirm">确认新密码</label>
            <span v-if="isDesktop" class="desktop-password-field">
              <input id="resetPasswordConfirm" v-model="form.resetConfirm" name="passwordConfirm" :type="reveal.resetConfirm ? 'text' : 'password'" minlength="8" autocomplete="new-password" placeholder="再次输入新密码" required data-desktop-password-enhanced="1">
              <button type="button" class="desktop-password-toggle" :aria-label="reveal.resetConfirm ? '隐藏密码' : '显示密码'" :aria-pressed="reveal.resetConfirm" @click="reveal.resetConfirm = !reveal.resetConfirm">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.3-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.3 6.5-9.5 6.5S2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.7" /></svg>
              </button>
            </span>
            <input v-else id="resetPasswordConfirm" v-model="form.resetConfirm" name="passwordConfirm" type="password" minlength="8" autocomplete="new-password" placeholder="再次输入新密码" required>
          </div>
        </div>
        <div class="auth-error" :class="{ 'desktop-auth-error': isDesktop }" role="alert" aria-live="assertive">{{ errors.reset }}</div>
        <div class="auth-notice auth-reset-feedback" role="status" aria-live="polite" :hidden="!shown.reset">{{ feedback.reset }}</div>
        <button type="submit" class="auth-primary" :disabled="submitting">{{ submitting ? '重置中…' : '确认重置' }}</button>
        <button type="button" class="auth-back-login-bottom" :disabled="submitting" @click="backToPasswordLogin">返回登录</button>
      </form>

      <div v-if="isDesktop" v-show="wechatOpen" id="authWechatPanel" class="auth-wechat-panel">
        <p class="auth-wechat-hint">电脑微信已登录时可快捷确认，也可使用手机微信扫码登录。</p>
        <div id="authWechatQr" class="auth-wechat-qr" aria-live="polite"><p class="auth-wechat-tip">勾选下方同意后打开微信官方授权</p></div>
        <label class="auth-legal-consent">
          <input id="authWechatLegal" v-model="form.wechatLegal" type="checkbox" @change="startWechat">
          <span>我已阅读并同意 <a href="/user-agreement.html" target="_blank" rel="noopener">《用户协议》</a>和<a href="/privacy.html" target="_blank" rel="noopener">《隐私政策》</a></span>
        </label>
        <div id="authWechatError" class="auth-error desktop-auth-error" role="alert" aria-live="assertive">{{ errors.wechat }}</div>
        <button type="button" class="auth-wechat-return" @click="closeWechat">返回邮箱登录</button>
      </div>
      <button type="button" class="auth-close" aria-label="关闭" @click="cancel">×</button>
    </section>
  </div>
</template>
